import React, { useMemo, useState } from "react";
import { Box, Chip, IconButton, Paper, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import ApartmentIcon from "@mui/icons-material/Apartment";
import BlockIcon from "@mui/icons-material/Block";
import EditIcon from "@mui/icons-material/Edit";
import GroupIcon from "@mui/icons-material/Group";
import RestoreFromTrashIcon from "@mui/icons-material/RestoreFromTrash";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { svSE, enUS } from "@mui/x-data-grid/locales";
import { useIntl } from "react-intl";
import { tokens } from "../../../assets/theme";
import { hasAnyRole } from "../../../auth/authUi";
import { useAuth } from "../../../auth/AuthContext";
import { CMMS_MANAGER_ROLES } from "../../cmms/cmmsRoles";
import { getAssetHierarchyPalette, getStatusChipSx, getTypeChipSx } from "../../cmms/assetHierarchyStyles";
import { CmmsListSkeleton } from "../../cmms/components/CmmsStates";
import messages from "../messages";

/*
 * The trades list.
 *
 * SparePartTypesDataGrid with a different noun, for the reason that grid gives for being a copy of
 * the storage locations one rather than a parameterised third use of it. No GridToolbar and no
 * remembered column state — a handful of columns on a settings tab have nothing to choose between.
 *
 * Sorted by name, which is also the order the endpoint returns and the order the clustered index
 * on (CompanyKey, Name) already reads in.
 *
 * Rows grow to fit their members and departments. A trade can hold a whole shift, and a
 * fixed-height row would clip the chips past the first line with nothing to say anyone was missing.
 */
export default function TradesDataGrid({
  trades,
  isLoading,
  showStatusColumn,
  onEdit,
  onMembers,
  onDepartments,
  onDeactivate,
  onRestore,
  emptyState,
}) {
  const theme = useTheme();
  const intl = useIntl();
  const colors = tokens(theme.palette.mode);
  const cmmsColors = getAssetHierarchyPalette(theme.palette.mode);
  const { roleIds } = useAuth();
  const language = localStorage.getItem("language") || "en";
  const translations = language === "sv" ? svSE : enUS;

  const [sortModel, setSortModel] = useState([{ field: "name", sort: "asc" }]);

  const canManage = hasAnyRole(roleIds, CMMS_MANAGER_ROLES);

  const columns = useMemo(() => {
    const cols = [
      {
        field: "name",
        headerName: intl.formatMessage(messages.tradeColumnName),
        flex: 1,
        minWidth: 200,
      },
      {
        field: "members",
        headerName: intl.formatMessage(messages.tradeColumnMembers),
        flex: 2,
        minWidth: 260,
        sortable: false,
        renderCell: (params) => {
          const members = params.row.members ?? [];

          if (members.length === 0) {
            return (
              <Typography variant="body2" sx={{ color: cmmsColors.mutedText }}>
                {intl.formatMessage(messages.tradeMembersNone)}
              </Typography>
            );
          }

          // In the server's order, which sorts by name in the mapping so the chips cannot shuffle
          // between two reads of the same trade.
          return (
            <Stack direction="row" useFlexGap flexWrap="wrap" gap={0.75} sx={{ width: "100%" }}>
              {members.map((member) => {
                const label = member.name || member.username || `#${member.userId}`;

                if (member.isTechnician) {
                  return <Chip key={member.userId} size="small" label={label} sx={getTypeChipSx(cmmsColors)} />;
                }

                // Only the stale ones explain themselves, as with the deactivated departments on
                // the notifications tab: a tooltip on every chip would bury the one that matters.
                return (
                  <Tooltip key={member.userId} title={intl.formatMessage(messages.tradeMemberNotTechnicianHint)}>
                    <Chip
                      size="small"
                      icon={<WarningAmberIcon />}
                      label={label}
                      sx={{ ...getStatusChipSx(cmmsColors, false), "& .MuiChip-icon": { color: "warning.main" } }}
                    />
                  </Tooltip>
                );
              })}
            </Stack>
          );
        },
      },
      {
        field: "departments",
        headerName: intl.formatMessage(messages.tradeColumnDepartments),
        flex: 1.5,
        minWidth: 220,
        sortable: false,
        renderCell: (params) => {
          const departments = params.row.departments ?? [];

          // Not a warning either: a trade with no departments still takes work orders assigned to
          // it by name, it only gets none by routing.
          if (departments.length === 0) {
            return (
              <Typography variant="body2" sx={{ color: cmmsColors.mutedText }}>
                {intl.formatMessage(messages.tradeDepartmentsNone)}
              </Typography>
            );
          }

          // The server's name order, and the same stale-chip treatment as a member who has lost
          // the role.
          return (
            <Stack direction="row" useFlexGap flexWrap="wrap" gap={0.75} sx={{ width: "100%" }}>
              {departments.map((department) => {
                const label = department.name || `#${department.assetNodeId}`;

                if (department.isActive) {
                  return <Chip key={department.assetNodeId} size="small" label={label} sx={getTypeChipSx(cmmsColors)} />;
                }

                return (
                  <Tooltip key={department.assetNodeId} title={intl.formatMessage(messages.tradeDepartmentInactiveHint)}>
                    <Chip
                      size="small"
                      icon={<WarningAmberIcon />}
                      label={label}
                      sx={{ ...getStatusChipSx(cmmsColors, false), "& .MuiChip-icon": { color: "warning.main" } }}
                    />
                  </Tooltip>
                );
              })}
            </Stack>
          );
        },
      },
    ];

    // Only while inactive rows can be on screen: a status column on a list that is all one
    // status is a column of identical chips.
    if (showStatusColumn) {
      cols.push({
        field: "isActive",
        headerName: intl.formatMessage(messages.columnActive),
        width: 110,
        sortable: false,
        renderCell: (params) => (
          <Chip
            size="small"
            label={intl.formatMessage(params.row.isActive ? messages.statusActive : messages.statusInactive)}
            sx={getStatusChipSx(cmmsColors, params.row.isActive)}
          />
        ),
      });
    }

    cols.push({
      field: "actions",
      headerName: "",
      width: 190,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box onClick={(event) => event.stopPropagation()}>
          {canManage &&
            <Tooltip title={intl.formatMessage(messages.tradeActionEdit)}>
              <IconButton
                size="small"
                aria-label={intl.formatMessage(messages.tradeActionEdit)}
                onClick={() => onEdit?.(params.row)}
                sx={{ backgroundColor: colors.primary[700], "&:hover": { backgroundColor: colors.primary[500] } }}
              >
                <EditIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          }
          {/*
            * On inactive rows too, like the rename beside it: a deactivated trade keeps its
            * members, and tidying them before it comes back is a reasonable thing to want.
            */}
          {canManage &&
            <Tooltip title={intl.formatMessage(messages.tradeActionMembers)}>
              <IconButton
                size="small"
                aria-label={intl.formatMessage(messages.tradeActionMembers)}
                onClick={() => onMembers?.(params.row)}
                sx={{ ml: 1, backgroundColor: colors.primary[700], "&:hover": { backgroundColor: colors.primary[500] } }}
              >
                <GroupIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          }
          {canManage &&
            <Tooltip title={intl.formatMessage(messages.tradeActionDepartments)}>
              <IconButton
                size="small"
                aria-label={intl.formatMessage(messages.tradeActionDepartments)}
                onClick={() => onDepartments?.(params.row)}
                sx={{ ml: 1, backgroundColor: colors.primary[700], "&:hover": { backgroundColor: colors.primary[500] } }}
              >
                <ApartmentIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          }
          {/*
            * Deactivate and its opposite share the slot, as on the other two tabs. An inactive row
            * is only on screen with "Show inactive" on, and the one thing left to do to it there
            * is bring it back.
            */}
          {canManage && params.row.isActive &&
            <Tooltip title={intl.formatMessage(messages.tradeActionDeactivate)}>
              <IconButton
                size="small"
                aria-label={intl.formatMessage(messages.tradeActionDeactivate)}
                onClick={() => onDeactivate?.(params.row)}
                sx={{ ml: 1, backgroundColor: colors.primary[700], "&:hover": { backgroundColor: colors.primary[500] } }}
              >
                <BlockIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          }
          {canManage && !params.row.isActive &&
            <Tooltip title={intl.formatMessage(messages.tradeActionRestore)}>
              <IconButton
                size="small"
                aria-label={intl.formatMessage(messages.tradeActionRestore)}
                onClick={() => onRestore?.(params.row)}
                sx={{ ml: 1, backgroundColor: colors.primary[700], "&:hover": { backgroundColor: colors.primary[500] } }}
              >
                <RestoreFromTrashIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          }
        </Box>
      ),
    });

    return cols;
  }, [intl, colors, cmmsColors, canManage, showStatusColumn, onEdit, onMembers, onDepartments, onDeactivate, onRestore]);

  const gridSlots = useMemo(
    () => ({
      loadingOverlay: () => (
        <Box sx={{ px: 2, pt: 1.5, width: "100%" }}>
          <CmmsListSkeleton rows={6} height={36} />
        </Box>
      ),
      // Only when the caller supplied one — an overlay rendering `undefined` would cover the grid
      // with nothing.
      ...(emptyState ? {
        noRowsOverlay: () => (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
            {emptyState}
          </Box>
        ),
      } : {}),
    }),
    [emptyState],
  );

  return (
    <Paper sx={{ position: "relative", borderRadius: "4px" }}>
      <Box
        height="60vh"
        sx={{
          "& .MuiDataGrid-root": {
            backgroundColor: colors.primary[400],
            borderColor: `${colors.primary[700]} !important`,
            borderRadius: "0px",
            fontSize: "0.95rem",
            border: "0 !important",
          },
          // Padding because the rows are auto height: without it a row of chips sits flush against
          // the lines above and below it.
          "& .MuiDataGrid-cell": { borderColor: `${colors.primary[700]} !important`, py: "10px" },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.primary[400],
            borderColor: `${colors.primary[700]} !important`,
          },
          "& .MuiDataGrid-virtualScroller": { backgroundColor: colors.primary[400] },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.primary[600],
          },
        }}
      >
        <DataGridPro
          loading={isLoading}
          slots={gridSlots}
          localeText={translations.components.MuiDataGrid.defaultProps.localeText}
          rows={trades || []}
          getRowId={(row) => row.id}
          columns={columns}
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          getRowHeight={() => "auto"}
          getEstimatedRowHeight={() => 52}
          disableRowSelectionOnClick
        />
      </Box>
    </Paper>
  );
}
